'use client';

import type { ReactNode } from 'react';
import { type VariantProps } from 'class-variance-authority';

import { chipVariants } from './Chip.variants';
import { cn } from '../../utils/cn/cn';

export type ChipProps = VariantProps<typeof chipVariants> & {
  className?: string;
  disabled?: boolean;
  /**
   * Simple text label for the chip, with standard padding and positioning
   */
  label?: string;
  /**
   * Content of the chip, can be any ReactNode, if you need precise control over the chip contents where `label` is not enough
   */
  children?: ReactNode;
  onClick?: () => void;
} & (
    | {
        onDelete: () => void;
        ariaLabelForDelete: string;
        iconClassName?: string;
      }
    | {
        iconClassName: string;
        iconPosition?: 'start' | 'end' | 'double';
      }
    | {}
  );

const MaybeButton = ({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) =>
  onClick ? (
    <button
      type="button"
      // NOTE: rounding is done to have a closely fitting focus indicator when using keyboard navigation
      className="inline-flex items-center rounded-l-full pl-0.5 last:rounded-r-full last:pr-0.5"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  ) : (
    <div className="inline-flex items-center pl-0.5 last:pr-0.5">{children}</div>
  );

export const Chip = ({
  className,
  color,
  disabled,
  children,
  label,
  onClick,
  selected,
  size,
  variant,
  ...otherProps
}: ChipProps) => (
  <div
    className={cn(
      chipVariants({
        className,
        color,
        disabled,
        selected,
        size,
        variant,
      }),
    )}
  >
    <MaybeButton onClick={onClick} disabled={disabled}>
      {'iconClassName' in otherProps &&
        (!('iconPosition' in otherProps) ||
          otherProps.iconPosition === 'start' ||
          otherProps.iconPosition === 'double') && <span className={otherProps.iconClassName} />}

      {label && <span className="px-1">{label}</span>}
      {children}

      {/* NOTE: mutually exclusive with onDelete, guaranteed on props type level */}
      {'iconClassName' in otherProps &&
        'iconPosition' in otherProps &&
        (otherProps.iconPosition === 'end' || otherProps.iconPosition === 'double') && (
          <span className={otherProps.iconClassName} />
        )}
    </MaybeButton>

    {/* NOTE: mutually exclusive with iconClassName, guaranteed on props type level */}
    {'onDelete' in otherProps && (
      <button
        type="button"
        className="hover:bg-color-grey inline-flex items-center justify-center rounded-r-full px-0.5"
        disabled={disabled}
        onClick={otherProps.onDelete}
        aria-label={otherProps.ariaLabelForDelete}
      >
        <span className="icon-emp-clear-input" aria-hidden />
      </button>
    )}
  </div>
);

Chip.displayName = 'Chip';
